/**
 * WarehouseReportParamsModal — parametry zestawienia magazynowego.
 *
 * Ten sam układ, co okno raportu sprzedaży, ale z filtrami, które mają sens na magazynie:
 * rozliczenie dokumentu (WZ bez faktury), magazyn i typ. Wartości do wyboru bierzemy ze
 * SŁOWNIKA MAGAZYNÓW, a nie z kartoteki — magazynów jest kilka i są stałe.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  HM_WAREHOUSE_REPORT_DIMENSIONS,
  hmWarehouseDimensionNeedsDetails,
  type HmWarehouseReportDimension,
} from '@saas/shared';

import { Button } from '@/components/Button';
import { Checkbox } from '@/components/Checkbox';
import { DatePicker } from '@/components/DatePicker';
import { Dropdown } from '@/components/Dropdown';
import { Modal } from '@/components/Modal';
import type { WarehouseReportParams } from '@/lib/handel-magazyn/warehouse-reports-api';

import { rangeFor, type RangePreset } from './HmListShell';

const MODAL_PRESETS: RangePreset[] = [
  'today',
  'yesterday',
  'thisMonth',
  'thisQuarter',
  'thisYear',
  'prevMonth',
  'last30',
  'last60',
  'last90',
  'all',
];

export function WarehouseReportParamsModal({
  open,
  initial,
  title,
  onClose,
  onSubmit,
}: {
  open: boolean;
  initial: WarehouseReportParams;
  title: string;
  onClose: () => void;
  onSubmit: (params: WarehouseReportParams) => void;
}) {
  const { t } = useTranslation();
  const [params, setParams] = useState<WarehouseReportParams>(initial);

  useEffect(() => {
    if (open) setParams(initial);
  }, [open, initial]);

  function set(patch: Partial<WarehouseReportParams>) {
    setParams((prev) => ({ ...prev, ...patch }));
  }

  function applyPreset(preset: RangePreset) {
    if (preset === 'custom') {
      set({ preset });
      return;
    }
    const range = rangeFor(preset);
    set({ preset, dateFrom: range.from, dateTo: range.to });
  }

  const dimensionOptions = useMemo(
    () =>
      HM_WAREHOUSE_REPORT_DIMENSIONS.map((dim) => ({
        value: dim,
        label: t(`tenant.handelMagazyn.warehouseReports.dimension.${dim}`),
        disabled: hmWarehouseDimensionNeedsDetails(dim) && !params.details,
      })),
    [t, params.details],
  );

  function toggleDetails(next: boolean) {
    // Wyłączenie dociągania zabiera dane wymiarowi, który na nich stoi.
    const losesData = hmWarehouseDimensionNeedsDetails(params.dim) && !next;
    set({
      details: next,
      ...(losesData ? { dim: 'none' as const } : {}),
      ...(next ? {} : { positions: false }),
    });
  }

  const canSubmit = !!params.dateFrom && !!params.dateTo && params.dateFrom <= params.dateTo;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      description={t('tenant.handelMagazyn.warehouseReports.subtitle')}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            {t('common.cancel')}
          </Button>
          <Button
            variant="primary"
            disabled={!canSubmit}
            onClick={() => onSubmit(params)}
            data-testid="hm-warehouse-report-submit"
          >
            {t('tenant.handelMagazyn.reports.run')}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        <section>
          <h3 className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-text-tertiary">
            {t('tenant.handelMagazyn.reports.period')}
          </h3>
          <div className="flex flex-wrap gap-1.5">
            {MODAL_PRESETS.map((preset) => (
              <Button
                key={preset}
                variant={params.preset === preset ? 'primary' : 'secondary'}
                size="sm"
                onClick={() => applyPreset(preset)}
                data-testid={`hm-warehouse-preset-${preset}`}
              >
                {t(`tenant.handelMagazyn.filters.preset.${preset}`)}
              </Button>
            ))}
          </div>
          <div className="mt-2 flex items-center gap-2">
            <DatePicker
              value={params.dateFrom}
              onChange={(value) => set({ preset: 'custom', dateFrom: value })}
              aria-label={t('tenant.handelMagazyn.filters.dateFrom')}
            />
            <span className="text-text-tertiary">—</span>
            <DatePicker
              value={params.dateTo}
              onChange={(value) => set({ preset: 'custom', dateTo: value })}
              aria-label={t('tenant.handelMagazyn.filters.dateTo')}
            />
          </div>
        </section>

        <section className="grid grid-cols-2 gap-3">
          <label className="block text-sm">
            <span className="mb-1 block text-text-secondary">
              {t('tenant.handelMagazyn.columns.status')}
            </span>
            <Dropdown
              value={params.status ?? ''}
              onChange={(value) => set({ status: (value as 'buffer' | 'issued') || null })}
              options={[
                { value: '', label: t('tenant.handelMagazyn.reports.allStatuses') },
                { value: 'issued', label: t('tenant.handelMagazyn.status.issued') },
                { value: 'buffer', label: t('tenant.handelMagazyn.status.buffer') },
              ]}
              data-testid="hm-warehouse-status"
            />
          </label>
          <label className="block text-sm">
            <span className="mb-1 block text-text-secondary">
              {t('tenant.handelMagazyn.warehouseReports.settlement')}
            </span>
            <Dropdown
              value={params.settlement ?? ''}
              onChange={(value) => set({ settlement: (value as 'settled' | 'unsettled') || null })}
              options={[
                { value: '', label: t('tenant.handelMagazyn.reports.allStatuses') },
                {
                  value: 'unsettled',
                  label: t('tenant.handelMagazyn.warehouseReports.status.unsettled'),
                },
                {
                  value: 'settled',
                  label: t('tenant.handelMagazyn.warehouseReports.status.settled'),
                },
              ]}
              data-testid="hm-warehouse-settlement"
            />
          </label>
        </section>

        <section className="space-y-2">
          <label className="block text-sm">
            <span className="mb-1 block text-text-secondary">
              {t('tenant.handelMagazyn.reports.summarizeBy')}
            </span>
            <Dropdown
              value={params.dim}
              onChange={(value) => set({ dim: value as HmWarehouseReportDimension })}
              options={dimensionOptions}
              data-testid="hm-warehouse-dimension"
            />
          </label>
          <Checkbox
            checked={params.details}
            onChange={(e) => toggleDetails(e.target.checked)}
            label={t('tenant.handelMagazyn.warehouseReports.withPositions')}
            hint={t('tenant.handelMagazyn.warehouseReports.withPositionsHint')}
            data-testid="hm-warehouse-details"
          />
        </section>
      </div>
    </Modal>
  );
}
